// Backend-for-frontend client. Talks to the Go Lambda behind /api/* (see BUILD.md).
// MOCK mode lets the built SPA run standalone (demos/screenshots) before the backend exists.
// Set VITE_MOCK=false once the backend is wired.

export type DemoSession = {
  email: string;
  firstName: string;
  maskedMobile: string;
  credits: number;
  cap: number;
  sendsUsed: number;
};

export type Stats = {
  queued: number;
  sent: number;
  notDelivered: number;
  responses: number;
  monthTotal: number;
};

const MOCK = import.meta.env.VITE_MOCK !== "false";

class ApiError extends Error {}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError((await res.text()) || `Request failed (${res.status})`);
  return (await res.json()) as T;
}

/** Step 2: on /verify load, request the OTP for the lead behind `token`. */
export async function requestOtp(token: string): Promise<{ maskedMobile: string; resendInSeconds: number }> {
  if (MOCK) return { maskedMobile: "+64 21 268 811", resendInSeconds: 55 };
  return post("/otp/request", { token });
}

/** Alternate step 2: switch the lead's mobile and send the OTP there instead. */
export async function changeMobile(
  token: string,
  mobile: string
): Promise<{ maskedMobile: string; resendInSeconds: number }> {
  if (MOCK) return { maskedMobile: mobile, resendInSeconds: 55 };
  return post("/otp/change-mobile", { token, mobile });
}

/** Step 3: verify the entered code; on success a demo session is issued. */
export async function verifyOtp(token: string, code: string): Promise<DemoSession> {
  if (MOCK) {
    if (!/^\d{4,6}$/.test(code)) throw new ApiError("Enter the code we sent you.");
    return {
      email: "placeholder@test.com",
      firstName: "Placeholder",
      maskedMobile: "+64 22 222 222",
      credits: 2,
      cap: 3,
      sendsUsed: 0,
    };
  }
  return post("/otp/verify", { token, code });
}

/** Step 4: send one demo SMS (server enforces the per-session cap). */
export async function sendSms(destination: string, message: string): Promise<{ remaining: number }> {
  if (MOCK) return { remaining: 2 };
  return post("/send", { destination, message });
}

export async function getStats(): Promise<Stats> {
  if (MOCK) return { queued: 0, sent: 0, notDelivered: 0, responses: 0, monthTotal: 0 };
  return post("/stats", {});
}

/** Current demo session (cookie-authenticated). Rejects when the session is
 *  missing/expired — the dashboard layout uses this as its route guard so the
 *  embedded web2sms components never run against a dead session (they hard-
 *  navigate to "/" on 401, which looks like an app reload). */
export async function getSession(): Promise<DemoSession> {
  if (MOCK) {
    return {
      email: "placeholder@test.com",
      firstName: "Placeholder",
      maskedMobile: "+64 22 222 222",
      credits: 2,
      cap: 3,
      sendsUsed: 0,
    };
  }
  const res = await fetch("/api/session", { credentials: "same-origin" });
  if (!res.ok) throw new ApiError(`No valid session (${res.status})`);
  return (await res.json()) as DemoSession;
}

export { ApiError };
